import * as React from 'react'
import moment from 'moment'
import { Link } from 'react-router'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { Skeleton, Empty } from 'antd'
import {
  API,
  ICustomerSummaryReport,
  ITopCustomersReport,
  PaymentFilter,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { Currency } from '../../../../../components/Currency'
import { PaymentListModal } from '../../../PaymentListModal'
import { parseAndDownloadCSV } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import Helmet from 'react-helmet'
import { Routes } from '../../../../../constants/Routes'
import { Track } from '../../../../../Track'
import { CustomerTotalSpend } from '../../../../../components/CustomerTotalSpend'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { Alert } from 'antd-v4'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  reportFilterStore?: ReportFilterStore
}

interface State {
  summary?: ICustomerSummaryReport
  topCustomers?: ITopCustomersReport
  selectedCustomerId?: string
}

@inject('currentCompanyStore', 'currentUserStore', 'reportFilterStore')
@observer
export class CustomersReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {}
  }

  componentDidMount() {
    this.loadReport()
  }

  loadReport() {
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!
    this.setState({ summary: undefined, topCustomers: undefined })
    const filter = {
      locationIds: selectedLocationIds,
      startDate: moment(startDate).startOf('day').toDate(),
      endDate: moment(endDate).endOf('day').toDate(),
    }
    const company = this.props.currentCompanyStore!.company!
    Track.startTimer('dashboard:reports_customersSummary')
    API.getCustomerSummary(company.id, filter).then((report) => {
      Track.track('dashboard:reports_customersSummary', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ summary: report })
    })
    Track.startTimer('dashboard:reports_customersTop')
    API.getTopCustomers(company.id, filter).then((report) => {
      Track.track('dashboard:reports_customersTop', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ topCustomers: report })
    })
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const topCustomers = this.state.topCustomers
    const filename = `customer-report-${startDate}-to-${endDate}`

    if (topCustomers) {
      const reportJSON: Array<{
        name: string
        spent?: string
        visits: string
      }> = []

      topCustomers.data.forEach((customer) => {
        reportJSON.push({
          name: `${customer.name}`,
          spent: this.canViewCustomerTotalSpend
            ? `$${(customer.total / 100).toFixed(2)}`
            : undefined,
          visits: `${customer.visits}`,
        })
      })

      parseAndDownloadCSV(filename, reportJSON)
    }
  }

  get canViewCustomerTotalSpend() {
    return !!this.props.currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      this.props.currentCompanyStore!.company!.id,
    )
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const summary = this.state.summary
    const topCustomers = this.state.topCustomers
    const { startDate, endDate } = this.props.reportFilterStore!

    const customerFilter = new PaymentFilter(company.id, 30, 0)
    if (this.state.selectedCustomerId) {
      customerFilter.setCustomerId(this.state.selectedCustomerId)
    }

    return (
      <>
        <Helmet>
          <title>Reports | Customers</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter maxRangeMonths={12} />

        {!summary || !topCustomers ? (
          <Skeleton active />
        ) : (
          <div>
            <div className='row'>
              <div className='col-md-6 col-sm-6 col-6'>
                <div className='rounded background-white'>
                  <div className='p-3'>
                    <div>
                      <b>Total customers</b>
                    </div>
                    <div className='text-muted mb-2'>All time</div>
                    <h3 className='mb-0'>{summary.data.total}</h3>
                  </div>
                </div>
              </div>
              <div className='col-md-6 col-sm-6 col-6'>
                <div className='rounded background-white'>
                  <div className='p-3'>
                    <div>
                      <b>New customers</b>
                    </div>
                    <div className='text-muted mb-2'>
                      {moment(startDate).format('MMM D')} to {moment(endDate).format('MMM D')}
                    </div>
                    <h3 className='mb-0'>{summary.data.new}</h3>
                  </div>
                </div>
              </div>
            </div>

            <br />

            <div className='rounded background-white'>
              <div className='card-body table-responsive'>
                <div>
                  <b>Top customers</b>
                </div>
                <div className='text-muted'>
                  View all customers <Link to={Routes.DASHBOARD_CUSTOMERS(company.id)}>here</Link>
                </div>

                <div className='mt-2 mb-2'>
                  {!this.canViewCustomerTotalSpend && (
                    <Alert
                      className='mt-3 mb-3'
                      type='warning'
                      showIcon
                      message={
                        <div>
                          <div>{`You do not have "${PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND}" permission.`}</div>
                          <div>Total Spend field will be removed.</div>
                        </div>
                      }
                    />
                  )}

                  <button className='btn btn-light btn-sm' onClick={() => this.export()}>
                    Export as csv
                  </button>
                </div>

                {topCustomers.data.length ? (
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Customer name</th>
                        <th className='text-right'>Total spend</th>
                        <th className='text-right'>Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCustomers.data.map((customer) => {
                        return (
                          <tr key={customer.id}>
                            <td>
                              <a onClick={() => this.setState({ selectedCustomerId: customer.id })}>
                                {customer.name || '(Anonymous)'}
                              </a>
                            </td>
                            <td className='text-right'>
                              <CustomerTotalSpend
                                canViewCustomerTotalSpend={this.canViewCustomerTotalSpend}
                                totalSpend={customer.total}
                              />
                            </td>
                            <td className='text-right'>{customer.visits} visits</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <Empty />
                )}
              </div>
            </div>

            {this.state.selectedCustomerId && (
              <PaymentListModal
                filter={customerFilter}
                onClose={() => this.setState({ selectedCustomerId: undefined })}
              />
            )}
          </div>
        )}
      </>
    )
  }
}
